import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AccusedRecord, CriminalWizardDraft } from '../types/criminalCaseRequest';
import type { CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import type { CivilAppellateWizardDraft } from '../types/civilAppellateCaseRequest';
import type { CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import type { CriminalAppellateWizardDraft } from '../types/criminalAppellateCaseRequest';
import type { NominationWizardDraft } from '../types/nominationCaseRequest';
import type { PublicPetitionWizardDraft } from '../types/publicPetitionCaseRequest';
import type { RightToInformationWizardDraft } from '../types/rightToInformationCaseRequest';
import type { AttorneyGeneralWizardDraft } from '../types/attorneyGeneralCaseRequest';
import type { ExtraditionMlaWizardDraft } from '../types/extraditionMlaCaseRequest';
import {
  DEFAULT_CRIMINAL_DRAFT,
  type CriminalWizardContextValue,
} from './CriminalRequestLayout';
import {
  DEFAULT_CRIMINAL_APPELLATE_DRAFT,
  type CriminalAppellateWizardContextValue,
} from './CriminalAppellateRequestLayout';
import {
  DEFAULT_CIVIL_ADVISE_DRAFT,
  type CivilAdviseWizardContextValue,
} from './CivilAdviseRequestLayout';
import {
  DEFAULT_CIVIL_LITIGATION_DRAFT,
  type CivilLitigationWizardContextValue,
} from './CivilLitigationRequestLayout';
import {
  DEFAULT_NOMINATION_DRAFT,
  type NominationWizardContextValue,
} from './NominationRequestLayout';
import {
  DEFAULT_PUBLIC_PETITION_DRAFT,
  type PublicPetitionWizardContextValue,
} from './PublicPetitionRequestLayout';
import {
  DEFAULT_RIGHT_TO_INFORMATION_DRAFT,
  type RightToInformationWizardContextValue,
} from './RightToInformationRequestLayout';
import {
  DEFAULT_ATTORNEY_GENERAL_DRAFT,
  type AttorneyGeneralWizardContextValue,
} from './AttorneyGeneralRequestLayout';
import {
  DEFAULT_EXTRADITION_MLA_DRAFT,
  type ExtraditionMlaWizardContextValue,
} from './ExtraditionMlaRequestLayout';
import * as criminalService from '../services/criminalCaseRequests';
import * as civilAdviseService from '../services/civilAdviseCaseRequests';
import * as civilLitigationService from '../services/civilLitigationCaseRequests';
import * as criminalAppellateService from '../services/criminalAppellateCaseRequests';
import * as nominationService from '../services/nominationCaseRequests';
import * as publicPetitionService from '../services/publicPetitionCaseRequests';
import * as rightToInformationService from '../services/rightToInformationCaseRequests';
import * as attorneyGeneralService from '../services/attorneyGeneralCaseRequests';
import * as extraditionMlaService from '../services/extraditionMlaCaseRequests';
import {
  DEFAULT_CIVIL_APPELLATE_DRAFT,
  type CivilAppellateWizardContextValue,
} from './CivilAppellateRequestLayout';

import * as civilAppellateService from '../services/civilAppellateCaseRequests';

import {
  civilAdviseToCivilAppellateSeed,
  civilAdviseToCivilLitigationSeed,
  civilAdviseToCriminalSeed,
  civilAdviseToExtraditionMlaSeed,
  civilAppellateToCivilAdviseSeed,
  civilAppellateToCivilLitigationSeed,
  civilAppellateToCriminalSeed,
  civilLitigationToCivilAdviseSeed,
  civilLitigationToCivilAppellateSeed,
  civilLitigationToCriminalSeed,
  civilAppellateToExtraditionMlaSeed,
  civilLitigationToExtraditionMlaSeed,
  criminalToCivilAdviseSeed,
  criminalToCivilAppellateSeed,
  criminalToCivilLitigationSeed,
  criminalToExtraditionMlaSeed,
} from '../utils/wizardDraftSeed';
import { resolveWizardFlow, type WizardFlowKind } from '../utils/wizardCategoryRoute';
import { persistWizardReturnTo } from '../utils/wizardReturnNavigation';
import {
  pickConnectedMatters,
  pickDraftIdentity,
  pickSharedCommonSeed,
} from '../utils/wizardCategorySwitchSeed';
import {
  readWizardCategorySwitchSnapshot,
  saveWizardCategorySwitchSnapshot,
} from '../utils/wizardCategorySwitchSnapshot';
import {
  findFirstInvalidWizardStep,
  wizardPathForStep,
} from '../utils/wizardFirstInvalidStep';

function newAccused(): AccusedRecord {
  return { id: crypto.randomUUID(), name: '', status: '', statusOther: '' };
}

function defaultCriminalDraft(): CriminalWizardDraft {
  return { ...DEFAULT_CRIMINAL_DRAFT, accused: [newAccused()] };
}

function defaultConnectedMatters(
  value: CivilAdviseWizardDraft['hasConnectedMatters'] | undefined,
): CivilAdviseWizardDraft['hasConnectedMatters'] {
  return value === 'yes' || value === 'no' ? value : 'no';
}

type AnyWizardDraft =
  | CriminalWizardDraft
  | CivilAdviseWizardDraft
  | CivilLitigationWizardDraft
  | CivilAppellateWizardDraft
  | ExtraditionMlaWizardDraft
  | CriminalAppellateWizardDraft
  | NominationWizardDraft
  | PublicPetitionWizardDraft
  | RightToInformationWizardDraft
  | AttorneyGeneralWizardDraft;

function pickAttorneyGeneralSeed(source: AnyWizardDraft): Partial<AttorneyGeneralWizardDraft> {
  const s = source as Record<string, unknown>;
  return {
    ...pickDraftIdentity(s),
    description: typeof s.description === 'string' ? s.description : '',
    provinceId: typeof s.provinceId === 'string' ? s.provinceId : '',
    provinceName: typeof s.provinceName === 'string' ? s.provinceName : '',
    districtId: typeof s.districtId === 'string' ? s.districtId : '',
    districtName: typeof s.districtName === 'string' ? s.districtName : '',
    caseSource: typeof s.caseSource === 'string' ? s.caseSource : '',
    yourReferenceNumber: typeof s.yourReferenceNumber === 'string' ? s.yourReferenceNumber : '',
    otherExternalReferences: typeof s.otherExternalReferences === 'string' ? s.otherExternalReferences : '',
    caseNumber: typeof s.caseNumber === 'string' ? s.caseNumber : '',
    hasConnectedMatters: pickConnectedMatters(s.hasConnectedMatters),
    connectedCaseNumber: typeof s.connectedCaseNumber === 'string' ? s.connectedCaseNumber : '',
    connectedAgdFileRef: typeof s.connectedAgdFileRef === 'string' ? s.connectedAgdFileRef : '',
    plaintiffName: typeof s.plaintiffName === 'string' ? s.plaintiffName : '',
    defendantName: typeof s.defendantName === 'string' ? s.defendantName : '',
    otherInvolvedInstitutions: typeof s.otherInvolvedInstitutions === 'string' ? s.otherInvolvedInstitutions : '',
    legalOfficerName: typeof s.legalOfficerName === 'string' ? s.legalOfficerName : '',
    legalOfficerContact: typeof s.legalOfficerContact === 'string' ? s.legalOfficerContact : '',
    subjectOfDispute: typeof s.subjectOfDispute === 'string' ? s.subjectOfDispute : '',
    incidentDateFrom: typeof s.incidentDateFrom === 'string' ? s.incidentDateFrom : '',
    incidentDateTo: typeof s.incidentDateTo === 'string' ? s.incidentDateTo : '',
    nextStepOfCaseId: typeof s.nextStepOfCaseId === 'string' ? s.nextStepOfCaseId : '',
    nextStepOfCaseName: typeof s.nextStepOfCaseName === 'string' ? s.nextStepOfCaseName : '',
    urgencyLevelId: typeof s.urgencyLevelId === 'string' ? s.urgencyLevelId : '',
    urgencyLevelName: typeof s.urgencyLevelName === 'string' ? s.urgencyLevelName : '',
    deadline: typeof s.deadline === 'string' ? s.deadline : '',
    urgencyJustification: typeof s.urgencyJustification === 'string' ? s.urgencyJustification : '',
    deliveryMethodIds: Array.isArray(s.deliveryMethodIds) ? (s.deliveryMethodIds as string[]) : [],
    deliveryMethods: Array.isArray(s.deliveryMethods) ? (s.deliveryMethods as string[]) : [],
    deliveryOther: typeof s.deliveryOther === 'string' ? s.deliveryOther : '',
  };
}

function pickRightToInformationSeed(source: AnyWizardDraft): Partial<RightToInformationWizardDraft> {
  const s = source as Record<string, unknown>;
  return {
    ...pickDraftIdentity(s),
    urgencyLevelId: typeof s.urgencyLevelId === 'string' ? s.urgencyLevelId : '',
    urgencyLevelName: typeof s.urgencyLevelName === 'string' ? s.urgencyLevelName : '',
    deadline: typeof s.deadline === 'string' ? s.deadline : '',
    urgencyJustification: typeof s.urgencyJustification === 'string' ? s.urgencyJustification : '',
  };
}

function pickNominationSeed(source: AnyWizardDraft): Partial<NominationWizardDraft> {
  const s = source as Record<string, unknown>;
  return {
    ...pickDraftIdentity(s),
    description: typeof s.description === 'string' ? s.description : '',
    caseNumber: typeof s.caseNumber === 'string' ? s.caseNumber : '',
    hasConnectedMatters: pickConnectedMatters(s.hasConnectedMatters),
    connectedCaseNumber: typeof s.connectedCaseNumber === 'string' ? s.connectedCaseNumber : '',
    connectedAgdFileRef: typeof s.connectedAgdFileRef === 'string' ? s.connectedAgdFileRef : '',
    urgencyLevelId: typeof s.urgencyLevelId === 'string' ? s.urgencyLevelId : '',
    urgencyLevelName: typeof s.urgencyLevelName === 'string' ? s.urgencyLevelName : '',
    deadline: typeof s.deadline === 'string' ? s.deadline : '',
    urgencyJustification: typeof s.urgencyJustification === 'string' ? s.urgencyJustification : '',
    deliveryMethodIds: Array.isArray(s.deliveryMethodIds) ? (s.deliveryMethodIds as string[]) : [],
    deliveryMethods: Array.isArray(s.deliveryMethods) ? (s.deliveryMethods as string[]) : [],
    deliveryOther: typeof s.deliveryOther === 'string' ? s.deliveryOther : '',
  };
}

function pickPublicPetitionSeed(source: AnyWizardDraft): Partial<PublicPetitionWizardDraft> {
  const s = source as Record<string, unknown>;
  return {
    ...pickDraftIdentity(s),
    description: typeof s.description === 'string' ? s.description : '',
    provinceId: typeof s.provinceId === 'string' ? s.provinceId : '',
    provinceName: typeof s.provinceName === 'string' ? s.provinceName : '',
    districtId: typeof s.districtId === 'string' ? s.districtId : '',
    districtName: typeof s.districtName === 'string' ? s.districtName : '',
    yourReferenceNumber:
      typeof s.yourReferenceNumber === 'string'
        ? s.yourReferenceNumber
        : typeof s.externalCaseReferenceNumber === 'string'
          ? s.externalCaseReferenceNumber
          : '',
    otherExternalReferences:
      typeof s.otherExternalReferences === 'string'
        ? s.otherExternalReferences
        : typeof s.otherExternalCaseReferenceNumber === 'string'
          ? s.otherExternalCaseReferenceNumber
          : '',
    caseNumber: typeof s.caseNumber === 'string' ? s.caseNumber : '',
    hasConnectedMatters: pickConnectedMatters(s.hasConnectedMatters),
    connectedCaseNumber: typeof s.connectedCaseNumber === 'string' ? s.connectedCaseNumber : '',
    connectedAgdFileRefAvailable:
      typeof s.connectedAgdFileRefAvailable === 'boolean'
        ? s.connectedAgdFileRefAvailable
        : Boolean(typeof s.connectedAgdFileRef === 'string' && s.connectedAgdFileRef.trim()),
    connectedAgdFileRef: typeof s.connectedAgdFileRef === 'string' ? s.connectedAgdFileRef : '',
    urgencyLevelId: typeof s.urgencyLevelId === 'string' ? s.urgencyLevelId : '',
    urgencyLevelName: typeof s.urgencyLevelName === 'string' ? s.urgencyLevelName : '',
    deadline: typeof s.deadline === 'string' ? s.deadline : '',
    urgencyJustification: typeof s.urgencyJustification === 'string' ? s.urgencyJustification : '',
    deliveryMethodIds: Array.isArray(s.deliveryMethodIds) ? (s.deliveryMethodIds as string[]) : [],
    deliveryMethods: Array.isArray(s.deliveryMethods) ? (s.deliveryMethods as string[]) : [],
    deliveryOther: typeof s.deliveryOther === 'string' ? s.deliveryOther : '',
  };
}

function pickCriminalAppellateSeed(source: AnyWizardDraft): Partial<CriminalAppellateWizardDraft> {
  const common = pickSharedCommonSeed(source as Record<string, unknown>);
  const connectedAgdFileRef =
    typeof common.connectedAgdFileRef === 'string' ? common.connectedAgdFileRef : '';
  return {
    draftRequestId: typeof common.draftRequestId === 'string' ? common.draftRequestId : '',
    referenceNo: typeof common.referenceNo === 'string' ? common.referenceNo : '',
    description: typeof common.description === 'string' ? common.description : '',
    provinceId: typeof common.provinceId === 'string' ? common.provinceId : '',
    provinceName: typeof common.provinceName === 'string' ? common.provinceName : '',
    districtId: typeof common.districtId === 'string' ? common.districtId : '',
    districtName: typeof common.districtName === 'string' ? common.districtName : '',
    yourReferenceNumber: typeof common.yourReferenceNumber === 'string' ? common.yourReferenceNumber : '',
    otherExternalReferences:
      typeof common.otherExternalReferences === 'string' ? common.otherExternalReferences : '',
    caseNumber: typeof common.caseNumber === 'string' ? common.caseNumber : '',
    hasConnectedMatters: pickConnectedMatters(common.hasConnectedMatters),
    connectedCaseNumber: typeof common.connectedCaseNumber === 'string' ? common.connectedCaseNumber : '',
    connectedAgdFileRef,
    connectedAgdFileRefAvailable: Boolean(connectedAgdFileRef.trim()),
    urgencyLevelId: typeof common.urgencyLevelId === 'string' ? common.urgencyLevelId : '',
    urgencyLevelName: typeof common.urgencyLevelName === 'string' ? common.urgencyLevelName : '',
    deadline: typeof common.deadline === 'string' ? common.deadline : '',
    urgencyJustification: typeof common.urgencyJustification === 'string' ? common.urgencyJustification : '',
    deliveryMethodIds: Array.isArray(common.deliveryMethodIds) ? (common.deliveryMethodIds as string[]) : [],
    deliveryMethods: Array.isArray(common.deliveryMethods) ? (common.deliveryMethods as string[]) : [],
    deliveryOther: typeof common.deliveryOther === 'string' ? common.deliveryOther : '',
    instituteType:
      common.instituteType === 'government' || common.instituteType === 'corporation'
        ? common.instituteType
        : 'government',
    corporateInstitute: typeof common.corporateInstitute === 'string' ? common.corporateInstitute : '',
    corporateInstituteName:
      typeof common.corporateInstituteName === 'string' ? common.corporateInstituteName : '',
    corporateInstituteOther:
      typeof common.corporateInstituteOther === 'string' ? common.corporateInstituteOther : '',
    corporateInstituteContactName:
      typeof common.corporateInstituteContactName === 'string' ? common.corporateInstituteContactName : '',
    corporateInstitutePrefix:
      typeof common.corporateInstitutePrefix === 'string' ? common.corporateInstitutePrefix : '',
  };
}

export type FileCategorySwitchInput = {
  fromFlow: WizardFlowKind;
  categoryId: string;
  categoryName: string;
  categoryCode?: string;
  patch?: Record<string, unknown>;
};

export type CaseRequestWizardContextValue = {
  activeFlow: WizardFlowKind;
  criminal: CriminalWizardContextValue;
  criminalAppellate: CriminalAppellateWizardContextValue;
  civilAdvise: CivilAdviseWizardContextValue;
  civilLitigation: CivilLitigationWizardContextValue;
  nomination: NominationWizardContextValue;
  publicPetition: PublicPetitionWizardContextValue;
  rightToInformation: RightToInformationWizardContextValue;
  attorneyGeneral: AttorneyGeneralWizardContextValue;
  extraditionMla: ExtraditionMlaWizardContextValue;
  civilAppellate: CivilAppellateWizardContextValue;
  switchFileCategory: (input: FileCategorySwitchInput) => void;
};

type WizardResumeState = {
  resumeFlow?: WizardFlowKind;
  seedDraft?: Partial<CriminalWizardDraft>;
  returnTo?: string;
};

function categoryClearFields() {
  return {
    subFileCategoryId: '',
    subFileCategoryName: '',
    applicableAreaIds: [] as string[],
    applicableAreas: [] as string[],
    documents: [] as unknown[],
  };
}

export default function CaseRequestLayout() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = (params.caseRequestId ?? searchParams.get('id') ?? '').trim();
  const [activeFlow, setActiveFlow] = useState<WizardFlowKind>('criminal');
  const [criminalDraft, setCriminalDraft] = useState<CriminalWizardDraft>(defaultCriminalDraft);
  const [criminalAppellateDraft, setCriminalAppellateDraft] = useState<CriminalAppellateWizardDraft>({
    ...DEFAULT_CRIMINAL_APPELLATE_DRAFT,
  });
  const [civilAdviseDraft, setCivilAdviseDraft] = useState<CivilAdviseWizardDraft>({
    ...DEFAULT_CIVIL_ADVISE_DRAFT,
  });
  const [civilLitigationDraft, setCivilLitigationDraft] = useState<CivilLitigationWizardDraft>({
    ...DEFAULT_CIVIL_LITIGATION_DRAFT,
  });
  const [nominationDraft, setNominationDraft] = useState<NominationWizardDraft>({
    ...DEFAULT_NOMINATION_DRAFT,
  });
  const [publicPetitionDraft, setPublicPetitionDraft] = useState<PublicPetitionWizardDraft>({
    ...DEFAULT_PUBLIC_PETITION_DRAFT,
  });
  const [rightToInformationDraft, setRightToInformationDraft] = useState<RightToInformationWizardDraft>({
    ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT,
  });
  const [attorneyGeneralDraft, setAttorneyGeneralDraft] = useState<AttorneyGeneralWizardDraft>({
    ...DEFAULT_ATTORNEY_GENERAL_DRAFT,
  });
  const [extraditionMlaDraft, setExtraditionMlaDraft] = useState<ExtraditionMlaWizardDraft>({
    ...DEFAULT_EXTRADITION_MLA_DRAFT,
  });

  useEffect(() => {
    persistWizardReturnTo(location.state);
  }, [location.state]);
  const [civilAppellateDraft, setCivilAppellateDraft] = useState<CivilAppellateWizardDraft>({
    ...DEFAULT_CIVIL_APPELLATE_DRAFT,
  });
  const [loadedResumeId, setLoadedResumeId] = useState<string | null>(null);
  const loadingDraft = Boolean(resumeId) && loadedResumeId !== resumeId;

  const seedTargetDraft = useCallback(
    (
      fromFlow: WizardFlowKind,
      toFlow: WizardFlowKind,
      source: AnyWizardDraft,
      categoryFields: {
        fileCategoryId: string;
        fileCategory: string;
        subFileCategoryId: string;
        subFileCategoryName: string;
        applicableAreaIds: string[];
        applicableAreas: string[];
        documents: unknown[];
      },
      patch: Record<string, unknown>,
    ): AnyWizardDraft | null => {
      const identity = pickDraftIdentity(source as Record<string, unknown>);
      const merged = { ...source, ...categoryFields, ...patch, ...identity };

      if (toFlow === 'criminal-appellate') {
        const next = {
          ...DEFAULT_CRIMINAL_APPELLATE_DRAFT,
          ...pickCriminalAppellateSeed(source),
          ...categoryFields,
          ...patch,
          ...identity,
        } as CriminalAppellateWizardDraft;
        setCriminalAppellateDraft(next);
        return next;
      }

      if (toFlow === 'nomination-requests') {
        const next = {
          ...DEFAULT_NOMINATION_DRAFT,
          ...pickNominationSeed(source),
          ...categoryFields,
          ...patch,
          ...identity,
        } as NominationWizardDraft;
        setNominationDraft(next);
        return next;
      }

      if (toFlow === 'public-petition') {
        const next = {
          ...DEFAULT_PUBLIC_PETITION_DRAFT,
          ...pickPublicPetitionSeed(source),
          ...categoryFields,
          ...patch,
          ...identity,
        } as PublicPetitionWizardDraft;
        setPublicPetitionDraft(next);
        return next;
      }

      if (toFlow === 'right-to-information') {
        const next = {
          ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT,
          ...pickRightToInformationSeed(source),
          ...categoryFields,
          ...patch,
          ...identity,
        } as RightToInformationWizardDraft;
        setRightToInformationDraft(next);
        return next;
      }

      if (toFlow === 'attorney-general') {
        const next = {
          ...DEFAULT_ATTORNEY_GENERAL_DRAFT,
          ...pickAttorneyGeneralSeed(source),
          ...categoryFields,
          ...patch,
          ...identity,
        } as AttorneyGeneralWizardDraft;
        setAttorneyGeneralDraft(next);
        return next;
      }

      if (toFlow === 'civil-advise') {
        let seed: Partial<CivilAdviseWizardDraft>;
        if (fromFlow === 'criminal') seed = criminalToCivilAdviseSeed(merged as CriminalWizardDraft);
        else if (fromFlow === 'civil-litigation') seed = civilLitigationToCivilAdviseSeed(merged as CivilLitigationWizardDraft);
        else if (fromFlow === 'civil-appellate') seed = civilAppellateToCivilAdviseSeed(merged as CivilAppellateWizardDraft);
        else seed = { ...pickSharedCommonSeed(source as Record<string, unknown>), ...categoryFields, ...patch, ...identity } as Partial<CivilAdviseWizardDraft>;
        const next = {
          ...DEFAULT_CIVIL_ADVISE_DRAFT,
          ...seed,
          ...categoryFields,
          ...patch,
          ...identity,
          hasConnectedMatters: defaultConnectedMatters(seed.hasConnectedMatters),
        } as CivilAdviseWizardDraft;
        setCivilAdviseDraft(next);
        return next;
      }

      if (toFlow === 'civil-litigation') {
        let seed: Partial<CivilLitigationWizardDraft>;
        if (fromFlow === 'criminal') seed = criminalToCivilLitigationSeed(merged as CriminalWizardDraft);
        else if (fromFlow === 'civil-advise') seed = civilAdviseToCivilLitigationSeed(merged as CivilAdviseWizardDraft);
        else if (fromFlow === 'civil-appellate') seed = civilAppellateToCivilLitigationSeed(merged as CivilAppellateWizardDraft);
        else seed = { ...pickSharedCommonSeed(source as Record<string, unknown>), ...categoryFields, ...patch, ...identity } as Partial<CivilLitigationWizardDraft>;
        const next = {
          ...DEFAULT_CIVIL_LITIGATION_DRAFT,
          ...seed,
          ...categoryFields,
          ...patch,
          ...identity,
          publicCorporationNames: (seed as CivilLitigationWizardDraft).publicCorporationNames?.length
            ? (seed as CivilLitigationWizardDraft).publicCorporationNames
            : [''],
        } as CivilLitigationWizardDraft;
        setCivilLitigationDraft(next);
        return next;
      }

      if (toFlow === 'extradition-mla') {
        let seed: Partial<ExtraditionMlaWizardDraft>;
        if (fromFlow === 'criminal') {
          seed = criminalToExtraditionMlaSeed(merged as CriminalWizardDraft);
        } else if (fromFlow === 'civil-advise') {
          seed = civilAdviseToExtraditionMlaSeed(merged as CivilAdviseWizardDraft);
        } else if (fromFlow === 'civil-litigation') {
          seed = civilLitigationToExtraditionMlaSeed(merged as CivilLitigationWizardDraft);
        } else if (fromFlow === 'civil-appellate') {
          seed = civilAppellateToExtraditionMlaSeed(merged as CivilAppellateWizardDraft);
        } else {
          seed = { ...pickSharedCommonSeed(source as Record<string, unknown>), ...categoryFields, ...patch, ...identity } as Partial<ExtraditionMlaWizardDraft>;
        }
        const next = {
          ...DEFAULT_EXTRADITION_MLA_DRAFT,
          ...seed,
          ...categoryFields,
          ...patch,
          ...identity,
          hasConnectedMatters: seed.hasConnectedMatters || 'no',
        } as ExtraditionMlaWizardDraft;
        setExtraditionMlaDraft(next);
        return next;
      }
      if (toFlow === 'civil-appellate') {
        let seed: Partial<CivilAppellateWizardDraft>;
        if (fromFlow === 'criminal') seed = criminalToCivilAppellateSeed(merged as CriminalWizardDraft);
        else if (fromFlow === 'civil-advise') seed = civilAdviseToCivilAppellateSeed(merged as CivilAdviseWizardDraft);
        else if (fromFlow === 'civil-litigation') seed = civilLitigationToCivilAppellateSeed(merged as CivilLitigationWizardDraft);
        else seed = { ...pickSharedCommonSeed(source as Record<string, unknown>), ...categoryFields, ...patch, ...identity } as Partial<CivilAppellateWizardDraft>;
        const next = {
          ...DEFAULT_CIVIL_APPELLATE_DRAFT,
          ...seed,
          ...categoryFields,
          ...patch,
          ...identity,
        } as CivilAppellateWizardDraft;
        setCivilAppellateDraft(next);
        return next;
      }

      if (toFlow === 'criminal') {
        let seed: Partial<CriminalWizardDraft>;
        if (fromFlow === 'civil-advise') seed = civilAdviseToCriminalSeed(merged as CivilAdviseWizardDraft);
        else if (fromFlow === 'civil-litigation') seed = civilLitigationToCriminalSeed(merged as CivilLitigationWizardDraft);
        else if (fromFlow === 'civil-appellate') seed = civilAppellateToCriminalSeed(merged as CivilAppellateWizardDraft);
        else seed = { ...pickSharedCommonSeed(source as Record<string, unknown>), ...categoryFields, ...patch, ...identity } as Partial<CriminalWizardDraft>;
        const next = {
          ...defaultCriminalDraft(),
          ...seed,
          ...categoryFields,
          ...patch,
          ...identity,
          accused: (seed as CriminalWizardDraft).accused?.length
            ? (seed as CriminalWizardDraft).accused
            : [newAccused()],
        } as CriminalWizardDraft;
        setCriminalDraft(next);
        return next;
      }

      return null;
    },
    [],
  );

  const draftForFlow = useCallback(
    (flow: WizardFlowKind): AnyWizardDraft => {
      if (flow === 'civil-advise') return civilAdviseDraft;
      if (flow === 'civil-litigation') return civilLitigationDraft;
      if (flow === 'criminal-appellate') return criminalAppellateDraft;
      if (flow === 'nomination-requests') return nominationDraft;
      if (flow === 'public-petition') return publicPetitionDraft;
      if (flow === 'right-to-information') return rightToInformationDraft;
      if (flow === 'attorney-general') return attorneyGeneralDraft;
      if (flow === 'extradition-mla') return extraditionMlaDraft;
      if (flow === 'civil-appellate') return civilAppellateDraft;
      return criminalDraft;
    },
    [
      civilAdviseDraft,
      civilAppellateDraft,
      civilLitigationDraft,
      criminalDraft,
      criminalAppellateDraft,
      extraditionMlaDraft,
      nominationDraft,
      publicPetitionDraft,
      rightToInformationDraft,
      attorneyGeneralDraft,
    ],
  );

  const applyPatchedDraftToFlow = useCallback((flow: WizardFlowKind, next: AnyWizardDraft) => {
    if (flow === 'civil-advise') {
      setCivilAdviseDraft(next as CivilAdviseWizardDraft);
    } else if (flow === 'civil-litigation') {
      setCivilLitigationDraft(next as CivilLitigationWizardDraft);
    } else if (flow === 'criminal-appellate') {
      setCriminalAppellateDraft(next as CriminalAppellateWizardDraft);
    } else if (flow === 'nomination-requests') {
      setNominationDraft(next as NominationWizardDraft);
    } else if (flow === 'public-petition') {
      setPublicPetitionDraft(next as PublicPetitionWizardDraft);
    } else if (flow === 'right-to-information') {
      setRightToInformationDraft(next as RightToInformationWizardDraft);
    } else if (flow === 'attorney-general') {
      setAttorneyGeneralDraft(next as AttorneyGeneralWizardDraft);
    } else if (flow === 'extradition-mla') {
      setExtraditionMlaDraft(next as ExtraditionMlaWizardDraft);
    } else if (flow === 'civil-appellate') {
      setCivilAppellateDraft(next as CivilAppellateWizardDraft);
    } else {
      setCriminalDraft(next as CriminalWizardDraft);
    }
  }, []);

  /**
   * After category change: navigate to the first step with unfilled mandatory fields.
   * Cross-flow switches pass forceStep=1 (new schema). Same-flow uses validation,
   * falling back to step 1 so we never leave the user on a late/review step.
   * Does not alter API `current_step` — that still updates only via draft save.
   */
  const navigateToFirstInvalidStepAfterCategoryChange = useCallback(
    (
      flow: WizardFlowKind,
      draft: AnyWizardDraft,
      options?: { forceStep?: number },
    ) => {
      const targetStep =
        options?.forceStep ?? findFirstInvalidWizardStep(flow, draft) ?? 1;

      const draftId = pickDraftIdentity(draft as Record<string, unknown>).draftRequestId.trim();
      const targetPath = wizardPathForStep(targetStep, draftId || undefined);
      const prevState =
        location.state && typeof location.state === 'object'
          ? (location.state as Record<string, unknown>)
          : {};

      navigate(targetPath, {
        replace: true,
        state: {
          ...prevState,
          resumeFlow: flow === 'unknown' ? undefined : flow,
          seedDraft: undefined,
        },
      });
    },
    [navigate, location.state],
  );

  const switchFileCategory = useCallback(
    ({ fromFlow: requestedFromFlow, categoryId, categoryName, categoryCode, patch = {} }: FileCategorySwitchInput) => {
      const fromFlow = activeFlow !== 'unknown' ? activeFlow : requestedFromFlow;
      const toFlow = resolveWizardFlow(categoryId, categoryName, categoryCode);
      const categoryFields = {
        fileCategoryId: categoryId,
        fileCategory: categoryName,
        ...categoryClearFields(),
      };

      if (!categoryId || toFlow === 'unknown' || toFlow === fromFlow) {
        const source = draftForFlow(fromFlow);
        const nextDraft = { ...source, ...categoryFields, ...patch } as AnyWizardDraft;
        applyPatchedDraftToFlow(fromFlow, nextDraft);
        const active = toFlow !== 'unknown' ? toFlow : fromFlow;
        navigateToFirstInvalidStepAfterCategoryChange(active, nextDraft);
        if (toFlow !== 'unknown') setActiveFlow(toFlow);
        return;
      }

      const source = draftForFlow(fromFlow);
      const nextDraft = seedTargetDraft(fromFlow, toFlow, source, categoryFields, patch);

      // Persist the switched draft so remount/refresh does not reload the old BE type and wipe UI state.
      const draftId = pickDraftIdentity(source as Record<string, unknown>).draftRequestId.trim();
      if (draftId && nextDraft) {
        saveWizardCategorySwitchSnapshot({
          draftRequestId: draftId,
          activeFlow: toFlow,
          draft: nextDraft as unknown as Record<string, unknown>,
          savedAt: Date.now(),
        });
      }

      // Navigate before setActiveFlow: shared step routes (e.g. step-6) redirect late steps
      // when activeFlow changes while the URL is still on the old wizard step.
      navigateToFirstInvalidStepAfterCategoryChange(
        toFlow,
        nextDraft ??
          ({
            ...categoryFields,
            ...pickDraftIdentity(source as Record<string, unknown>),
          } as AnyWizardDraft),
        { forceStep: 1 },
      );
      setActiveFlow(toFlow);
    },
    [
      activeFlow,
      draftForFlow,
      applyPatchedDraftToFlow,
      navigateToFirstInvalidStepAfterCategoryChange,
      seedTargetDraft,
    ],
  );

  useEffect(() => {
    if (!resumeId || loadedResumeId === resumeId) {
      return;
    }

    const resumeState = location.state as WizardResumeState | null;
    const resumeFlow = resumeState?.resumeFlow;
    const seedDraft = resumeState?.seedDraft;
    let mounted = true;

    const finish = () => {
      if (mounted) setLoadedResumeId(resumeId);
    };

    const applyCriminalDraft = (data: Partial<CriminalWizardDraft>) => {
      setCriminalDraft({
        ...defaultCriminalDraft(),
        ...data,
        accused: data.accused?.length ? data.accused : [newAccused()],
      });
      setActiveFlow('criminal');
    };

    const loadCriminal = () =>
      criminalService.getCriminalWizardDraft(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          applyCriminalDraft(r.data);
          return true;
        }
        return false;
      });

    const loadCriminalAppellate = () =>
      criminalAppellateService.getCriminalAppellateRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setCriminalAppellateDraft({ ...DEFAULT_CRIMINAL_APPELLATE_DRAFT, ...r.data });
          setActiveFlow('criminal-appellate');
          return true;
        }
        return false;
      });

    const loadRightToInformation = () =>
      rightToInformationService.getRightToInformationRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setRightToInformationDraft({ ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT, ...r.data });
          setActiveFlow('right-to-information');
          return true;
        }
        return false;
      });

    const loadAttorneyGeneral = () =>
      attorneyGeneralService.getAttorneyGeneralRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setAttorneyGeneralDraft({ ...DEFAULT_ATTORNEY_GENERAL_DRAFT, ...r.data });
          setActiveFlow('attorney-general');
          return true;
        }
        return false;
      });

    const loadNomination = () =>
      nominationService.getNominationRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setNominationDraft({ ...DEFAULT_NOMINATION_DRAFT, ...r.data });
          setActiveFlow('nomination-requests');
          return true;
        }
        return false;
      });

    const loadPublicPetition = () =>
      publicPetitionService.getPublicPetitionRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setPublicPetitionDraft({
            ...DEFAULT_PUBLIC_PETITION_DRAFT,
            ...r.data,
            hasConnectedMatters: r.data.hasConnectedMatters || 'no',
          });
          setActiveFlow('public-petition');
          return true;
        }
        return false;
      });

    const loadCivilAdvise = () =>
      civilAdviseService.getCivilAdviseRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setCivilAdviseDraft({
            ...DEFAULT_CIVIL_ADVISE_DRAFT,
            ...r.data,
            hasConnectedMatters: defaultConnectedMatters(r.data.hasConnectedMatters),
          });
          setActiveFlow('civil-advise');
          return true;
        }
        return false;
      });

    const loadCivilLitigation = () =>
      civilLitigationService.getCivilLitigationRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setCivilLitigationDraft({
            ...DEFAULT_CIVIL_LITIGATION_DRAFT,
            ...r.data,
            publicCorporationNames: r.data.publicCorporationNames.length
              ? r.data.publicCorporationNames
              : [''],
          });
          setActiveFlow('civil-litigation');
          return true;
        }
        return false;
      });

    const loadExtraditionMla = () =>
      extraditionMlaService.getExtraditionMlaRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setExtraditionMlaDraft({
            ...DEFAULT_EXTRADITION_MLA_DRAFT,
            ...r.data,
            hasConnectedMatters: r.data.hasConnectedMatters || 'no',
          });
          setActiveFlow('extradition-mla');
          return true;
        }
        return false;
      });
    const loadCivilAppellate = () =>
      civilAppellateService.getCivilAppellateRequestDetails(resumeId).then((r) => {
        if (!mounted) return false;
        if (r.success && r.data) {
          setCivilAppellateDraft({ ...DEFAULT_CIVIL_APPELLATE_DRAFT, ...r.data });
          setActiveFlow('civil-appellate');

          return true;
        }
        return false;
      });

    void (async () => {
      try {
        const applySwitchedSnapshot = (switched: NonNullable<ReturnType<typeof readWizardCategorySwitchSnapshot>>) => {
          const draft = switched.draft;
          if (switched.activeFlow === 'criminal') {
            applyCriminalDraft(draft as Partial<CriminalWizardDraft>);
          } else if (switched.activeFlow === 'criminal-appellate') {
            setCriminalAppellateDraft({
              ...DEFAULT_CRIMINAL_APPELLATE_DRAFT,
              ...(draft as Partial<CriminalAppellateWizardDraft>),
            });
            setActiveFlow('criminal-appellate');
          } else if (switched.activeFlow === 'civil-advise') {
            setCivilAdviseDraft({
              ...DEFAULT_CIVIL_ADVISE_DRAFT,
              ...(draft as Partial<CivilAdviseWizardDraft>),
              hasConnectedMatters: defaultConnectedMatters(
                (draft as Partial<CivilAdviseWizardDraft>).hasConnectedMatters,
              ),
            });
            setActiveFlow('civil-advise');
          } else if (switched.activeFlow === 'civil-litigation') {
            const lit = draft as Partial<CivilLitigationWizardDraft>;
            setCivilLitigationDraft({
              ...DEFAULT_CIVIL_LITIGATION_DRAFT,
              ...lit,
              publicCorporationNames: lit.publicCorporationNames?.length
                ? lit.publicCorporationNames
                : [''],
            });
            setActiveFlow('civil-litigation');
          } else if (switched.activeFlow === 'civil-appellate') {
            setCivilAppellateDraft({
              ...DEFAULT_CIVIL_APPELLATE_DRAFT,
              ...(draft as Partial<CivilAppellateWizardDraft>),
            });
            setActiveFlow('civil-appellate');
          } else if (switched.activeFlow === 'extradition-mla') {
            setExtraditionMlaDraft({
              ...DEFAULT_EXTRADITION_MLA_DRAFT,
              ...(draft as Partial<ExtraditionMlaWizardDraft>),
              hasConnectedMatters:
                (draft as Partial<ExtraditionMlaWizardDraft>).hasConnectedMatters || 'no',
            });
            setActiveFlow('extradition-mla');
          } else if (switched.activeFlow === 'nomination-requests') {
            setNominationDraft({
              ...DEFAULT_NOMINATION_DRAFT,
              ...(draft as Partial<NominationWizardDraft>),
            });
            setActiveFlow('nomination-requests');
          } else if (switched.activeFlow === 'public-petition') {
            setPublicPetitionDraft({
              ...DEFAULT_PUBLIC_PETITION_DRAFT,
              ...(draft as Partial<PublicPetitionWizardDraft>),
              hasConnectedMatters:
                (draft as Partial<PublicPetitionWizardDraft>).hasConnectedMatters || 'no',
            });
            setActiveFlow('public-petition');
          } else if (switched.activeFlow === 'right-to-information') {
            setRightToInformationDraft({
              ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT,
              ...(draft as Partial<RightToInformationWizardDraft>),
            });
            setActiveFlow('right-to-information');
          } else if (switched.activeFlow === 'attorney-general') {
            setAttorneyGeneralDraft({
              ...DEFAULT_ATTORNEY_GENERAL_DRAFT,
              ...(draft as Partial<AttorneyGeneralWizardDraft>),
            });
            setActiveFlow('attorney-general');
          }
        };

        if (resumeFlow === 'criminal' && seedDraft) {
          applyCriminalDraft(seedDraft);
          window.history.replaceState({}, document.title);
          return;
        }

        const loadByFlow = async (flow: WizardFlowKind): Promise<boolean> => {
          if (flow === 'right-to-information') return loadRightToInformation();
          if (flow === 'attorney-general') return loadAttorneyGeneral();
          if (flow === 'extradition-mla') return loadExtraditionMla();
          if (flow === 'civil-appellate') return loadCivilAppellate();
          if (flow === 'civil-litigation') return loadCivilLitigation();
          if (flow === 'civil-advise') return loadCivilAdvise();
          if (flow === 'criminal-appellate') return loadCriminalAppellate();
          if (flow === 'nomination-requests') return loadNomination();
          if (flow === 'public-petition') return loadPublicPetition();
          if (flow === 'criminal') return loadCriminal();
          return false;
        };

        if (resumeFlow && resumeFlow !== 'unknown') {
          if (await loadByFlow(resumeFlow)) return;
          // After a category switch, BE may still be the old type until save.
          // Fall back to the in-session switched draft instead of cascading wrong-type GETs.
          const switched = readWizardCategorySwitchSnapshot(resumeId);
          if (switched && switched.activeFlow === resumeFlow) {
            applySwitchedSnapshot(switched);
            return;
          }
        }

        if (await loadCriminal()) return;
        if (await loadCriminalAppellate()) return;
        if (await loadNomination()) return;
        if (await loadPublicPetition()) return;
        if (await loadRightToInformation()) return;
        if (await loadAttorneyGeneral()) return;
        if (await loadExtraditionMla()) return;
        if (await loadCivilAppellate()) return;
        if (await loadCivilLitigation()) return;
        await loadCivilAdvise();
      } finally {
        finish();
      }
    })();

    return () => {
      mounted = false;
    };
  }, [resumeId, loadedResumeId, location.state]);

  const contextValue = useMemo<CaseRequestWizardContextValue>(
    () => ({
      activeFlow,
      switchFileCategory,
      criminal: {
        draft: criminalDraft,
        setDraft: setCriminalDraft,
        reset: () => setCriminalDraft(defaultCriminalDraft()),
      },
      criminalAppellate: {
        draft: criminalAppellateDraft,
        setDraft: setCriminalAppellateDraft,
        reset: () => setCriminalAppellateDraft({ ...DEFAULT_CRIMINAL_APPELLATE_DRAFT }),
      },
      civilAdvise: {
        draft: civilAdviseDraft,
        setDraft: setCivilAdviseDraft,
        reset: () => setCivilAdviseDraft({ ...DEFAULT_CIVIL_ADVISE_DRAFT }),
      },
      civilLitigation: {
        draft: civilLitigationDraft,
        setDraft: setCivilLitigationDraft,
        reset: () =>
          setCivilLitigationDraft({
            ...DEFAULT_CIVIL_LITIGATION_DRAFT,
            publicCorporationNames: [''],
          }),
      },
      nomination: {
        draft: nominationDraft,
        setDraft: setNominationDraft,
        reset: () => setNominationDraft({ ...DEFAULT_NOMINATION_DRAFT }),
      },
      publicPetition: {
        draft: publicPetitionDraft,
        setDraft: setPublicPetitionDraft,
        reset: () => setPublicPetitionDraft({ ...DEFAULT_PUBLIC_PETITION_DRAFT }),
      },
      rightToInformation: {
        draft: rightToInformationDraft,
        setDraft: setRightToInformationDraft,
        reset: () => setRightToInformationDraft({ ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT }),
      },
      attorneyGeneral: {
        draft: attorneyGeneralDraft,
        setDraft: setAttorneyGeneralDraft,
        reset: () => setAttorneyGeneralDraft({ ...DEFAULT_ATTORNEY_GENERAL_DRAFT }),
      },
      extraditionMla: {
        draft: extraditionMlaDraft,
        setDraft: setExtraditionMlaDraft,
        reset: () => setExtraditionMlaDraft({ ...DEFAULT_EXTRADITION_MLA_DRAFT }),
      },
      civilAppellate: {
        draft: civilAppellateDraft,
        setDraft: setCivilAppellateDraft,
        reset: () => setCivilAppellateDraft({ ...DEFAULT_CIVIL_APPELLATE_DRAFT }),
      },
    }),
    [
      activeFlow,
      switchFileCategory,
      criminalDraft,
      criminalAppellateDraft,
      civilAdviseDraft,
      civilLitigationDraft,
      nominationDraft,
      publicPetitionDraft,
      rightToInformationDraft,
      attorneyGeneralDraft,
      extraditionMlaDraft,
      civilAppellateDraft,
    ],
  );

  if (loadingDraft) {
    return <div className="pg-sub">Loading case request…</div>;
  }

  return <Outlet context={contextValue} />;
}
